/**
 * Shared combobox option utility functions used by both Elements and React packages.
 * Only framework-agnostic functions belong here.
 */

import type { IPktComboboxOption, TPktComboboxDisplayValue } from 'shared-types/combobox'

/**
 * Finds an option by its value, falling back to label match.
 * Value matches are prioritized over label matches to avoid ambiguity
 * when an option's label matches another option's value.
 */
export const findOptionByValue = (
  options: IPktComboboxOption[],
  value: string | null,
): IPktComboboxOption | null => {
  if (!value) return null
  return (
    options.find((option) => option.value === value) ||
    options.find((option) => option.label === value) ||
    null
  )
}

/**
 * Finds the index of an option by its value, falling back to label match.
 */
export const findOptionIndex = (options: IPktComboboxOption[], value: string | null): number => {
  if (!value) return -1
  const byValue = options.findIndex((option) => option.value === value)
  if (byValue >= 0) return byValue
  return options.findIndex((option) => option.label === value)
}

/**
 * Filters options by a search string, matching against fulltext.
 */
export const filterOptionsBySearch = (
  options: IPktComboboxOption[],
  search: string | null,
): IPktComboboxOption[] => {
  if (!search) return [...options]
  const searchLower = search.toLowerCase()
  return options.filter((option) => {
    const fulltext = option.fulltext || option.value + (option.label || '') + (option.prefix || '')
    return fulltext.toLowerCase().includes(searchLower)
  })
}

/**
 * Builds the fulltext search string for an option.
 */
export const buildFulltext = (option: IPktComboboxOption): string => {
  return option.value + (option.label || '') + (option.prefix || '')
}

/**
 * Checks if the maximum number of selections has been reached.
 */
export const isMaxSelectionReached = (selectedCount: number, maxlength: number | null): boolean => {
  return maxlength !== null && selectedCount >= maxlength
}

/**
 * Returns the display text for an option based on the displayValueAs setting.
 */
export const getOptionDisplayText = (
  option: IPktComboboxOption | null,
  displayValueAs: TPktComboboxDisplayValue,
): string => {
  if (!option) return ''
  switch (displayValueAs) {
    case 'prefixAndValue':
      return `${option.prefix || ''} ${option.value}`.trim()
    case 'value':
      return option.value
    case 'label':
    default:
      return option.label || option.value
  }
}

/**
 * Parses a value prop (string or string[]) into a normalized array.
 */
export const parseValueToArray = (
  value: string | string[] | null | undefined,
  multiple: boolean,
): string[] => {
  if (!value) return []
  if (Array.isArray(value)) {
    return multiple ? value : value.length > 0 ? [value[0]] : []
  }
  if (multiple) {
    return value.split(',').filter(Boolean)
  }
  return [value]
}

/**
 * Finds typeahead matches from options based on search input.
 * Returns filtered options and the best autocomplete suggestion.
 */
export const findTypeaheadMatches = (
  options: IPktComboboxOption[],
  search: string,
): {
  filtered: IPktComboboxOption[]
  suggestion: IPktComboboxOption | null
} => {
  if (!search) return { filtered: [...options], suggestion: null }

  const searchLower = search.toLowerCase()
  const filtered = options.filter((option) => option.fulltext?.toLowerCase().includes(searchLower))

  const suggestion =
    filtered.find(
      (option) => !option.selected && option.label?.toLowerCase().startsWith(searchLower),
    ) || null

  return { filtered, suggestion }
}

/**
 * Determines the user info message based on search state and matches.
 */
export const getSearchInfoMessage = (
  searchValue: string,
  selectedValues: string[],
  options: IPktComboboxOption[],
  allowUserInput: boolean,
): { addValueText: string | null; userInfoMessage: string } => {
  if (!searchValue.trim()) {
    return { addValueText: null, userInfoMessage: '' }
  }

  const searchLower = searchValue.trim().toLowerCase()
  const matchedValue = selectedValues.find((v) => v.toLowerCase() === searchLower)
  const matchedOptions = options.filter(
    (option) =>
      option.label?.toLowerCase().includes(searchLower) ||
      option.value.toLowerCase().includes(searchLower),
  )
  const exactMatch = matchedOptions.find(
    (option) =>
      option.label?.toLowerCase() === searchLower || option.value.toLowerCase() === searchLower,
  )

  if ((matchedOptions.length === 0 || !exactMatch) && allowUserInput) {
    return { addValueText: searchValue, userInfoMessage: '' }
  }
  if (matchedOptions.length === 0 && !allowUserInput) {
    return { addValueText: null, userInfoMessage: 'Ingen treff i søket' }
  }
  if (matchedValue) {
    return { addValueText: null, userInfoMessage: 'Verdien er allerede valgt' }
  }
  return { addValueText: null, userInfoMessage: '' }
}
